import {useHttpClient} from '@raison/network-facade';
import {RootLayout, RsnButton, RsnDialog, RsnInput} from '@raison/ui-kit';
import {useSuspenseQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import {absurd} from 'fp-ts/lib/function';
import React, {useMemo, useState} from 'react';
import {compose} from 'redux';
import {CountryPicker, getCountrySync, SomeCountryCode} from '~/entities/countries';
import {useCurrencyFormat, usePrimaryCurrencyFormat, useRateToPrimaryCurrency} from '~/entities/currencies';
import {TransactionHolder} from '~/entities/operations-crypto';
import {OperationDetail} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history/model/TransactionDetailsPath';
import {ServiceProvider, useUserInfoQueryOptions} from '~/entities/user';
import {
    InvalidCodeDialog,
    PinErrorCode,
    SmsDeliveryStatus,
    SmsErrorStatus,
    SusActivityDialog,
    TwoFaCodeForm,
    TwoFaMethodChoiceView,
    TwoFaProvider,
    useResendTimer,
    useSmsStatusPolling,
} from '~/features/two-factor';
import {
    allFieldsCompleteRecipe,
    connectFieldWith,
    connectSubmitToImmerForm,
    fieldBlurRecipe,
    fieldsCompleteRecipe,
    FormState,
    ImmerSetState,
    Issues,
    useImmerState,
    userInputRecipe,
    validateFormState,
} from '~/library/form-models.v1';
import {Outcome} from '~/library/types';
import {useAppDispatch} from '~/shared/store';
import {SendTxIssues, submitSendTxForm} from '../api/submitSendTxForm';
import {submitUsualSignedSendTransaction} from '../api/submitUsualSignedSendTransaction';
import {define} from '../lib/define';
import {useInHistoryStateNamespace} from '../lib/useInHistoryState';
import {NavigationEntryPoint, useNavigateFromEntry} from '../lib/useNavigateWithCount';
import {SendTxResources} from '../model/SendTxContainer';
import {useSendTxResources} from '../model/SendTxResourcesContext';
import {OrderId} from '../model/TrTx';
import {useErrorHandler} from '../model/useErrorHandler';
import {FeeCalculationType, SendTxIntroResult, SendTxIntroValues} from './SendTxIntro';
import {OrderPlacedInfoPage} from './TxPlacedPage';

function SendUsualTxView({
    resources,
    formState,
    actions: act,
    picker,
}: {
    resources: SendTxResources;
    formState: SendUsualTxForm;
    actions: {
        fieldBlur: (field: keyof Values) => void;
        userInput: (updatedFields: Partial<Values>) => void;
        clickedSend: () => void;
        pickerToggled: (opened: boolean, pickerName: PickerName) => void;
        completeFields: () => void;
    };
    picker: PickerName | undefined;
}) {
    const hasImperialFinanceAllianceCorp = resources.user.serviceProviders.some(
        sp => sp.serviceProvider === ServiceProvider.ImperialFinanceAllianceCorp && sp.status === 'signed'
    );

    const t = {
        Fee: 'Fee',
        FullName: 'Full name',
        BeneficiaryNameExample: 'Example: John Doe',
        BeneficiaryEmail: 'Beneficiary email',
        Country: 'Country',
        Send: 'Send',
        InsufficientFunds: 'Due to insufficient funds, the transaction fee will be deducted from the transfer amount.',
        Sending: 'Sending',
        TransactionFee: 'Transaction fee',
        TotalAmount: 'Total amount',
        ApproxValue: 'Approx value',
        Required: 'This field is required',
        MaxParticipantName: `Should have ${140} characters or less`,
        OnlyAlphanumeric: 'Can contain only letters, digits, dots and spaces',
    };

    const {hasIssues, hasServerIssues, values, ...form} = validateFormState(formState, (values: Values) => {
        const issues: Issues<Values> = {};

        if (!/^[a-zA-Z0-9 .]{1,140}$/.test(values.beneficiaryName)) {
            if (values.beneficiaryName === '') {
                issues.beneficiaryName = t.Required;
            } else if (values.beneficiaryName.length > 140) {
                issues.beneficiaryName = t.MaxParticipantName;
            } else {
                issues.beneficiaryName = t.OnlyAlphanumeric;
            }
        }

        if (hasImperialFinanceAllianceCorp && values.beneficiaryCountry === null) {
            issues.beneficiaryCountry = 'This field is required';
        }
        return {issues, values};
    });
    const canSubmit = !hasIssues && !hasServerIssues && !form.isSubmitting;

    const handleSubmit = () => {
        if (!canSubmit) {
            act.completeFields();
            return;
        }

        act.clickedSend();
    };

    const connectField = connectFieldWith(form, act.fieldBlur);

    const connectPicker = (itsName: PickerName) => ({
        opened: itsName === picker,
        onDropdownToggle: (opened: boolean) => {
            act.pickerToggled(opened, itsName);
        },
    });

    return (
        <form className='contents' onSubmit={ev => (ev.preventDefault(), handleSubmit())}>
            <RsnInput
                label={<RsnInput.Label subtext={t.BeneficiaryNameExample}>{t.FullName}</RsnInput.Label>}
                value={values.beneficiaryName}
                onChange={ev => act.userInput({beneficiaryName: ev.target.value})}
                {...connectField('beneficiaryName')}
            />
            {hasImperialFinanceAllianceCorp && (
                <CountryPicker
                    format={'alpha3'}
                    label={t.Country}
                    placeholder='Select country'
                    value={values.beneficiaryCountry}
                    onChange={value => act.userInput({beneficiaryCountry: value})}
                    {...connectField('beneficiaryCountry')}
                    {...connectPicker(PickerName.beneficiaryCountry)}
                />
            )}

            <div className='mt-auto flex flex-col gap-5'>
                <RsnButton.Primary type='submit'>{t.Send}</RsnButton.Primary>
            </div>
        </form>
    );
}

export type PickerName = (typeof PickerName)[keyof typeof PickerName];
export const PickerName = {
    beneficiaryCountry: 'beneficiaryCountry',
} satisfies {
    [K in keyof Values]?: K;
};

type Values = SendOpUsualValues;
export type SendOpUsualValues = SendUsualTxForm['values'];

export type SendUsualTxForm = ReturnType<typeof SendUsualTxForm>;
export const SendUsualTxForm = () => {
    const state = FormState({
        beneficiaryName: '',
        beneficiaryCountry: define.nullable<SomeCountryCode>(),
    });
    return state;
};

export function SendUsualTxDirector({
    resources,
    formState,
    intro,
    entry,
    setFormState,
    onSubmitProblem,
    updateResources,
}: {
    setFormState: ImmerSetState<SendUsualTxForm>;
    onSubmitProblem: (issues: Issues<SendTxIntroValues>) => void;
    updateResources: () => void;
    resources: SendTxResources;
    entry: NavigationEntryPoint;
    intro: SendTxIntroResult;
    formState: SendUsualTxForm;
}) {
    const navigate = useNavigateFromEntry(entry);
    const navigateBack = () => navigate(-1);
    const useInHistoryState = useInHistoryStateNamespace('send_usual_flow', navigate);
    const [direction, setDirection] = useInHistoryState(SendUsualTxDirection, {view: Step.form});

    const dispatch = useAppDispatch();

    const queryOfUser = useSuspenseQuery(useUserInfoQueryOptions());

    const handleError = useErrorHandler();
    const http = useHttpClient();

    const {startPolling} = useSmsStatusPolling();
    const [smsForm, setSmsForm] = useImmerState(() => FormState({codeFromSms: ''}));
    const [timer$, setTimer] = useResendTimer({seconds: 60});

    const [confirm, setConfirm] = useState<{qty: string; th: TransactionHolder} | null>(null);

    const submitForm = connectSubmitToImmerForm(setFormState)(async (provider: TwoFaProvider, repeated = false) => {
        if (formState.submitInitAt > formState.submitDoneAt) return;

        const {id: accountId, type: accountType} = resources.asset;

        // Pre-fetching resources in case the data is stale
        setTimeout(() => {
            updateResources();
        });

        setSmsForm(form => {
            form.values.codeFromSms = '';
            form.serverIssues.codeFromSms = undefined;
        });

        setTimer.pending();
        const response = await submitSendTxForm(http, accountId, accountType, {
            ...formState.values,
            address: intro.address,
            comment: intro.comment,
            feeAmount: intro.feeAmount,
            amount: intro.amountToBeReceived,

            repeated,
            provider,
        }).catch(crash => {
            gotSubmitOutcome({crash});
            return handleError(crash);
        });
        switch (response.status) {
            case 200:
            case 201:
                gotSubmitOutcome({value: response.json});
                break;
            case 400:
                gotSubmitOutcome({problem: response.json});
                break;
        }

        function gotSubmitOutcome({
            value: th,
            problem: issues,
            crash,
        }: Outcome<TransactionHolder, Issues<SendTxIssues>>) {
            if (crash) {
                setTimer.failure(crash);
            }
            if (th) {
                setTimer.success(th.expires_in);
                setConfirm({qty: intro.amountToBeReceived.toString(), th});
                setDirection({view: Step.twoFaCode, provider}, {replace: true});
                if (provider.kind === 'sms') {
                    startPolling({
                        tenantId: th.tenant_uuid,
                        onError: onPollingError,
                        ...(repeated ? {onSuccess: onRepeatedPollingSuccess} : null),
                    });
                }
            }

            if (issues) {
                setFormState(form => {
                    form.serverIssues = issues;
                });
            }
            if (issues && SendTxIntroValues.fieldNames.some(field => issues[field])) {
                // FIXME: this navigation back to first screen is very fragile
                // it should rely on location indices or require redesign of the flow
                if (direction.view === '2fa-pick' || direction.view === '2fa-code') {
                    navigate(-2);
                }
                onSubmitProblem(issues);
            }
        }

        function onPollingError(error: SmsErrorStatus) {
            setTimer.success(0);
            setDirection(current => {
                return current.view === Step.twoFaCode ? {...current, delivery: error} : current;
            });
        }

        function onRepeatedPollingSuccess() {
            setDirection(current => {
                return current.view === Step.twoFaCode ? {...current, delivery: 'DELIVERED'} : current;
            });
        }
    });

    function togglePicker(opened: boolean, pickerName: PickerName) {
        if (direction.view !== Step.form) {
            return direction;
        }
        if (opened) {
            setDirection({...direction, picker: pickerName});
            return;
        }
        setFormState(fieldsCompleteRecipe(pickerName));
        navigate(-1);
    }

    if (direction.view === Step.finish) {
        const {txId, qty} = direction;
        return (
            <OrderPlacedInfoPage
                amount={qty}
                currency={resources.asset}
                goToDetails={() => executeOperationFinishNavigation(txId, navigate)}
                direction={'out'}
                details={'Transfer request submitted.\nProcessing may take some time.'}
            />
        );
    }

    if (direction.view === Step.twoFaPick) {
        return (
            <RootLayout common title='Confirmation' navigateBack={navigateBack}>
                <TwoFaMethodChoiceView
                    onMessagePick={method => submitForm({kind: 'sms', method})}
                    onAppPick={() => submitForm({kind: 'otp'})}
                    onActivationPick={() => setDirection(dir => ({...dir, dialog: 'settings-prompt'}))}
                    onMethodPickerOpen={() => setDirection(dir => ({...dir, dialog: 'method-picker'}))}
                    onDialogClose={() => navigate(-1)}
                    isActivationPromptOpen={direction.dialog === 'settings-prompt'}
                    isMethodPickerOpen={direction.dialog === 'method-picker'}
                    isAppActivated={queryOfUser.data.otpEnabled}
                />
            </RootLayout>
        );
    }

    if (direction.view === Step.twoFaCode && confirm) {
        const {th: holder, qty} = confirm;
        const {provider} = direction;

        const submitTransactionSignature = connectSubmitToImmerForm(setSmsForm)(async () => {
            const response = await submitUsualSignedSendTransaction(http, {
                codeFromSms: smsForm.values.codeFromSms,
                holder,
                wallet: resources.asset,
            }).catch(crash => {
                gotConfirmationOutcome({crash});
                return handleError(crash);
            });
            switch (response.status) {
                case 'Ok':
                    gotConfirmationOutcome({value: response.datum});
                    return;
                case 'BadRequest':
                    gotConfirmationOutcome({problem: response.datum});
                    return;
                case 'Abort':
                    return;
                default:
                    absurd(response);
            }
        });

        function gotConfirmationOutcome({value: txn, problem: issue}: Outcome<{id: OrderId}, string>) {
            if (txn) {
                setDirection({view: Step.finish, qty, txId: txn.id}, {replace: true});
            }
            if (issue === PinErrorCode.ExpiredCode) {
                setSmsForm(form => {
                    form.serverIssues = {codeFromSms: 'This code has expired'};
                });
            }
            if (issue === PinErrorCode.InvalidCode) {
                const onClose = () => {
                    setSmsForm(form => void (form.values.codeFromSms = ''));
                    TwoFaCodeForm.focusOTPInput();
                };
                dispatch(InvalidCodeDialog.open({onClose}));
            }
            if (issue === PinErrorCode.SusActivity) {
                dispatch(SusActivityDialog.open());
            }
        }

        const isSubmittingOtpForm = smsForm.submitInitAt > smsForm.submitDoneAt;

        return (
            <RootLayout common title='Confirmation' navigateBack={navigateBack}>
                <TwoFaCodeForm.ForCryptoTx
                    userPhone={queryOfUser.data.username}
                    provider={provider}
                    timer$={timer$}
                    onRetryIntent={() => submitForm(provider, true)}
                    otp={smsForm.values.codeFromSms}
                    onOtpChange={value => setSmsForm(userInputRecipe({codeFromSms: value}))}
                    onInputComplete={() => submitTransactionSignature()}
                    isPending={isSubmittingOtpForm}
                    issue={smsForm.serverIssues.codeFromSms}
                    onMethodChangeIntent={() => navigate(-1)}
                    deliveryInfo={direction.delivery ?? null}
                    onDeliveryInfoDismiss={() => navigate(-1)}
                />
            </RootLayout>
        );
    }

    return (
        <RootLayout common title={`Send ${resources.asset.code}`} navigateBack={navigateBack}>
            <SendUsualTxView
                resources={resources}
                formState={formState}
                actions={{
                    fieldBlur: compose(setFormState, fieldBlurRecipe),
                    userInput: compose(setFormState, userInputRecipe),
                    clickedSend: () => {
                        setDirection(current => ({...current, dialog: 'recipt'}));
                    },
                    pickerToggled: togglePicker,
                    completeFields: () => {
                        setFormState(allFieldsCompleteRecipe());
                    },
                }}
                picker={'picker' in direction ? direction.picker : undefined}
            />

            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {direction.dialog === 'recipt' && (
                    <ReciptDialog
                        onConfirm={() => {
                            setDirection({view: Step.twoFaPick}, {replace: true});
                        }}
                        values={formState.values}
                        intro={intro}
                    />
                )}
            </RsnDialog.Controller>
        </RootLayout>
    );
}

type Step = (typeof Step)[keyof typeof Step];
const Step = {
    form: 'form',
    twoFaPick: '2fa-pick',
    twoFaCode: '2fa-code',
    finish: 'finish',
} as const;

const SendUsualTxDirection = type.never
    .or({
        view: type.unit(Step.form),
        picker: type.enumerated(...Object.values(PickerName)).optional(),
    })
    .or({
        view: type.unit(Step.twoFaPick),
    })
    .or({
        view: type.unit(Step.twoFaCode),
        provider: TwoFaProvider,
        delivery: SmsDeliveryStatus.optional(),
    })
    .or({
        view: type.unit(Step.finish),
        qty: type.string,
        txId: OrderId,
    })
    .and({
        'dialog?': "'settings-prompt' | 'recipt' | 'method-picker'",
    });

function ReciptDialog({values, intro, onConfirm}: {values: Values; intro: SendTxIntroResult; onConfirm: () => void}) {
    const {asset} = useSendTxResources();

    const t = {
        ConfirmDetails: 'Confirm details',
        FromAccount: 'From account',
        ToAddress: 'To address',
        PayFeeFrom: 'Pay fee from',
        Comment: 'Details',
        FullName: 'Full name',
        Country: 'Country',
        FeeHandling: 'Fee handling',
        TransactionFee: 'Transaction fee',
        YouSpend: 'You spend',
        ApproxValue: 'Approx value',
        BeneficiaryGets: 'Beneficiary gets',
        Confirm: 'Confirm',
        FeeHandlingName: {
            included: 'Included',
            on_top: 'On top',
        } satisfies {[K in FeeCalculationType]: string},
    };

    const rate = useRateToPrimaryCurrency({code: asset.code, decimals: asset.decimals});
    const {format: formatAsPrimary} = usePrimaryCurrencyFormat();
    const {format: formatAsSubject} = useCurrencyFormat(asset);
    const {format: formatAsSubjectBlockchain} = useCurrencyFormat(asset.blockchain);

    const countryObject = useMemo(() => {
        const countryCode = values.beneficiaryCountry;
        return countryCode && getCountrySync(countryCode);
    }, [values.beneficiaryCountry]);

    const isOnOwnBlockchain = asset.blockchain.currencyCode === asset.code;

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader>{t.ConfirmDetails}</RsnDialog.CenteredHeader>
            <RsnDialog.Body align={'left'}>
                <div className='flex flex-col gap-4 rounded-[10px] bg-neutral-20 p-5'>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.FromAccount}>{asset.name}</OperationDetail>
                        <OperationDetail label={t.ToAddress}>
                            <span className='break-all'>{intro.address}</span>
                        </OperationDetail>
                        {!isOnOwnBlockchain && (
                            <OperationDetail label={t.PayFeeFrom}>
                                <span className='break-all'>{asset.blockchain.name}</span>
                            </OperationDetail>
                        )}
                        <OperationDetail label={t.Comment}>{intro.comment}</OperationDetail>
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.FullName}>{values.beneficiaryName}</OperationDetail>
                        {countryObject && <OperationDetail label={t.Country}>{countryObject.name}</OperationDetail>}
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.FeeHandling}>
                            {t.FeeHandlingName[intro.feeCalculationType]}
                        </OperationDetail>
                        <OperationDetail label={t.TransactionFee}>
                            {formatAsSubjectBlockchain(intro.feeAmount)}
                        </OperationDetail>
                        {isOnOwnBlockchain && (
                            <OperationDetail label={t.YouSpend}>
                                {formatAsSubject(intro.amountToBeSent)}
                            </OperationDetail>
                        )}
                        <OperationDetail label={t.ApproxValue}>
                            {formatAsPrimary(intro.amountToBeSent.multipliedBy(rate), {approx: true})}
                        </OperationDetail>
                        <OperationDetail label={t.BeneficiaryGets}>
                            {formatAsSubject(intro.amountToBeReceived)}
                        </OperationDetail>
                    </OperationDetail.Layout>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer column>
                <RsnButton.Primary onClick={onConfirm}>{t.Confirm}</RsnButton.Primary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}
